import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import BusinessIcon from "@mui/icons-material/Business";
import MapIcon from "@mui/icons-material/Map";
import PublicIcon from "@mui/icons-material/Public";
import {
  formatCurrency,
  formatRenewalPeriod,
  formatVehicleScope,
  formatZoneCoverageType,
  formatPermitDate,
  formatUsageFeeApplicability,
} from "common/format";
import type { PermitType, ZoneCoordinate } from "common/types/permits";

export type PermitTypeWithZones = PermitType & { zones: ZoneCoordinate[] };

export interface PermitTypeCardProps {
  permitType: PermitTypeWithZones;
  onViewZones: (permitType: PermitTypeWithZones) => void;
}

interface InfoItemProps {
  icon: React.ReactNode;
  label: string;
  value: string;
  description?: string;
}

const InfoItem: React.FC<InfoItemProps> = ({ icon, label, value, description }) => (
  <Box>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary", mb: 1 }}>
      {icon}
      <Typography variant="caption" fontWeight={500} textTransform="uppercase">
        {label}
      </Typography>
    </Box>
    <Typography variant="body2" fontWeight={500}>
      {value}
    </Typography>
    {description && (
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
        {description}
      </Typography>
    )}
  </Box>
);

export const PermitTypeCard: React.FC<PermitTypeCardProps> = ({ permitType, onViewZones }) => {
  const hasZones = permitType.zones.length > 0;
  const isJurisdictionWide = permitType.zoneCoverageType === "jurisdiction_wide";
  const isPerAccount = permitType.vehicleScope === "per_account";
  const hasRequiredDocs = permitType.requiredDocumentTypes && permitType.requiredDocumentTypes.length > 0;
  const usageFeeNotice = formatUsageFeeApplicability(permitType.usageFeeApplicability);

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" component="h3" fontWeight={600}>
              {permitType.name}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {permitType.description}
            </Typography>
          </Box>
          <Chip
            icon={<LockOutlinedIcon sx={{ fontSize: 14 }} />}
            label="Read-only"
            size="small"
            sx={{
              bgcolor: "grey.100",
              color: "text.secondary",
              ml: 2,
            }}
          />
        </Box>

        {/* Primary Info Grid - 4 columns */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={6} sm={3}>
            <InfoItem
              icon={<AttachMoneyIcon sx={{ fontSize: 16 }} />}
              label="Permit Fee"
              value={`$${formatCurrency(permitType.pricePerRenewalPeriod)}`}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <InfoItem
              icon={<CalendarTodayIcon sx={{ fontSize: 16 }} />}
              label="Start Date"
              value={formatPermitDate(permitType.validFrom)}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <InfoItem
              icon={<CalendarTodayIcon sx={{ fontSize: 16 }} />}
              label="End Date"
              value={formatPermitDate(permitType.validTo)}
            />
          </Grid>
          <Grid item xs={6} sm={3}>
            <InfoItem
              icon={<AutorenewIcon sx={{ fontSize: 16 }} />}
              label="Renewal"
              value={formatRenewalPeriod(permitType.renewalPeriod)}
            />
          </Grid>
        </Grid>

        {/* Applicability Section - 2 columns */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6}>
            <InfoItem
              icon={isPerAccount ? <BusinessIcon sx={{ fontSize: 16 }} /> : <DirectionsCarIcon sx={{ fontSize: 16 }} />}
              label="Applicability"
              value={formatVehicleScope(permitType.vehicleScope)}
              description={
                isPerAccount
                  ? "One permit covers all vehicles under this account"
                  : "Each vehicle requires its own permit"
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <InfoItem
              icon={<AutorenewIcon sx={{ fontSize: 16 }} />}
              label="Auto Assignment"
              value={permitType.isAutomaticVehicleAssignmentEnabled ? "Enabled" : "Disabled"}
              description={
                permitType.isAutomaticVehicleAssignmentEnabled
                  ? "Vehicles are automatically assigned to this permit"
                  : "Vehicles must be manually assigned"
              }
            />
          </Grid>
        </Grid>

        {/* Coverage Area & Required Documentation - 2 columns */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {/* Coverage Area */}
          <Grid item xs={12} sm={hasRequiredDocs ? 6 : 12}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary", mb: 0.5 }}>
              <MapIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption" fontWeight={500} textTransform="uppercase">
                Coverage Area
              </Typography>
            </Box>
            {isJurisdictionWide ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PublicIcon sx={{ fontSize: 18, color: "primary.main" }} />
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {formatZoneCoverageType(permitType.zoneCoverageType)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Valid throughout the entire jurisdiction
                  </Typography>
                </Box>
              </Box>
            ) : hasZones ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <Button size="small" variant="outlined" startIcon={<MapIcon />} onClick={() => onViewZones(permitType)}>
                  View Zones on Map
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Valid in {permitType.zones.length} designated zone{permitType.zones.length !== 1 ? "s" : ""}
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No zones configured
              </Typography>
            )}
          </Grid>

          {/* Required Documentation */}
          {hasRequiredDocs && (
            <Grid item xs={12} sm={6}>
              <Typography
                variant="caption"
                fontWeight={500}
                textTransform="uppercase"
                color="text.secondary"
                sx={{ display: "block", mb: 0.5 }}
              >
                Required Documentation
              </Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {permitType.requiredDocumentTypes?.map((docType) => (
                  <Chip key={docType.id} label={docType.name} size="small" sx={{ bgcolor: "grey.100" }} />
                ))}
              </Box>
            </Grid>
          )}
        </Grid>

        {/* Usage Fee Notice */}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {usageFeeNotice}
        </Typography>
      </CardContent>
    </Card>
  );
};

export default PermitTypeCard;
